import { useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';

import {
  Anchor,
  Box,
  PropertyCard as ConstellationPropertyCard,
  Heading,
  List,
  Paragraph,
  Popover,
  Text,
  TextButton,
  TriggerText,
} from '@zillow/constellation-10';

import { LoadingImage } from '@zg-rentals/loading-components';
import { formatAddress } from '@zg-rentals/zillow-rental-manager/utils';

import { useScreenSize } from '../../../../../hooks';
import { useGetListingDetails } from '../../../../../hooks/queries/properties';
import accessUtils from '../../../../../utils/accessUtils';
import numberUtils from '../../../../../utils/numberUtils';
import propertyUtils from '../../../../../utils/propertyUtils';

import AddPropertyModal from '../../../../modals/AddPropertyModal';
import RentalProtectionSelectionsModal from '../../../../modals/RentalProtectionSelectionsModal';

import RentGuaranteeShieldStar from '../../../../../assets/RentGuaranteeShieldStar.svg';
import { propertyPhotoStyles } from '../styles';
import { addressDetailsStyles, verticalDividerStyles } from '../styles';

import {
  emitApplicationsClickEvent,
  EVENT_IDS as REVIEW_PAGE_EVENT_IDS,
} from '../../../../../analytics/events/reviewPage';
import {
  emitRegulationsCTAClickEvent,
  EVENT_IDS as TOTAL_MONTHLY_PRICE_WITH_FEE_EVENT_IDS,
} from '../../../../../analytics/events/singleFamilyTotalMonthlyPrice';
import { useAuth } from '../../../../AuthProvider';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

const nullPlaceholder = '--';

type PropertyCardProps = {
  previewListingCTA?: React.ReactNode;
};

export const baseRentPopoverContents = ({
  currentProperty,
  user,
  triggerObject,
  triggerLocation,
}: {
  user: ZrmClickstreamUser | null;
  currentProperty?: ZrmClickstreamListing;
  triggerObject: string;
  triggerLocation: string;
}) => {
  return {
    header: (
      <Heading level={2} textStyle="body-lg-bold">
        Base rent
      </Heading>
    ),
    body: (
      <Paragraph>
        This amount reflects your base monthly rent only. To comply with{' '}
        <Anchor
          target="_blank"
          href="https://www.zillow.com/rentals-network/fee-transparency-regulations"
          onClick={() => {
            emitRegulationsCTAClickEvent({
              currentProperty,
              user,
              triggerObject,
              triggerLocation,
            });
          }}
          rel="noopener noreferrer"
          data-eventid={TOTAL_MONTHLY_PRICE_WITH_FEE_EVENT_IDS.REGULATIONS_CTA}
        >
          fee transparency regulations
        </Anchor>{' '}
        in your area, any required monthly fees you&apos;ve entered will be automatically combined with base rent after
        publishing to calculate and display a &ldquo; total monthly price&rdquo; on your listing. This helps renters
        understand the full monthly cost of living in the home.
      </Paragraph>
    ),
  };
};

const styles = {
  streetText: {
    mobile: 'body-bold',
    desktop: 'heading-md',
  },
  streetTextMF: {
    mobile: 'heading-sm',
    desktop: 'heading-sm',
  },
  stateText: {
    mobile: 'body-sm',
    desktop: 'body',
  },
  stateTextMF: {
    mobile: 'body-sm-bold',
    desktop: 'body-sm-bold',
  },
  details: {
    mobile: 'body-xs',
    desktop: 'body-bold',
  },
  detailsText: {
    mobile: 'body-xs',
    desktop: 'body',
  },
  button: {
    mobile: 'sm',
    desktop: 'md',
  },
} as const;

const getBedroomDisplay = (value: number | string) => {
  if (value === 0 || value === '0') {
    return 'Studio';
  }

  return value;
};

const PropertyCard = ({ previewListingCTA }: PropertyCardProps) => {
  const router = useRouter();
  const { user } = useAuth();
  const { isScreenMobile } = useScreenSize();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails, isLoading } = useGetListingDetails(listingAlias);

  const isInReactivationFlow = accessUtils.canAccessMLReactivationFlow({
    lastActivatedMs: listingDetails?.lastActivatedMs,
    isFeed: listingDetails?.isFeed,
    isMultifamily: listingDetails?.isMultifamily,
  });

  const [displayAddPropertyModal, setDisplayAddPropertyModal] = useState(false);
  const [displayRentalProtectionSelectionsModal, setDisplayRentalProtectionSelectionsModal] = useState(false);

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible,
  });

  const selectedCoverageOption = listingDetails?.rentGuarantee?.premiumCoverages?.find((option) => option.isSelected);
  // oxlint-disable-next-line oxc/erasing-op -- pre-existing operator-precedence bug, left as-is; behavior fix tracked separately
  const rentalProtectionPremium = selectedCoverageOption?.premium || 0 / 100;

  const breakpoint = isScreenMobile ? 'mobile' : 'desktop';

  const {
    totalNumberOfActiveApplications = 0,
    totalNumberOfDeclinedApplications = 0,
    totalNumberOfWithdrawnApplications = 0,
  } = listingDetails?.applicationsSummary?.rentalApplicationData || {};
  const totalNumberOfAllApplications =
    totalNumberOfDeclinedApplications + totalNumberOfWithdrawnApplications + totalNumberOfActiveApplications;

  const isRentalProtectionEnabled = listingDetails?.rentGuarantee?.isEnabled;
  const floorplans = listingDetails?.floorPlans || [];
  const isStudio = floorplans?.[0]?.numBedrooms === 0;
  const price =
    numberUtils.formatPriceFromApi(floorplans?.[0]?.lowPrice?.toString(), floorplans?.[0]?.highPrice?.toString()) || 0;
  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });
  const { formattedStreet, formattedCityStateZip } = formatAddress({
    street: listingDetails?.street,
    city: listingDetails?.city,
    state: listingDetails?.state,
    zip: listingDetails?.zip,
    unit: listingDetails?.unit,
  });

  const handleEditButtonClick = (isAdminTools = false) => {
    if (isAdminTools) {
      setDisplayAddPropertyModal(true);

      return;
    }
  };

  const handleApplicationsClick = () => {
    emitApplicationsClickEvent({
      user,
      currentProperty: listingDetails,
    });
  };

  const renderAddressDetails = () => {
    return (
      <Box css={addressDetailsStyles}>
        <Box data-testid="AddressDetailsSFContainer">
          <Text
            textStyle={styles.streetText[breakpoint]}
            css={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 'normal',
            }}
            asChild
          >
            {breakpoint === 'mobile' ? <p>{formattedStreet}</p> : <h2>{formattedStreet}</h2>}
          </Text>
          <Text
            textStyle={styles.stateText[breakpoint]}
            css={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              marginBottom: '100',
              md: { marginBottom: '300' },
            }}
            asChild
          >
            <p>{formattedCityStateZip}</p>
          </Text>
          <Box css={{ display: 'flex' }}>
            <List.Root appearance="none">
              <List.Item css={{ display: 'inline' }} data-testid="WizardModularListing-Review-Price">
                <Text textStyle={styles.details[breakpoint]}>
                  {`${price ? `$${numberUtils.prettyPrintNumber(price)}` : propertyUtils.getFloorPlanDescription({ units: listingDetails?.units }).displayPrice || nullPlaceholder}`}
                </Text>
                <Text textStyle={styles.detailsText[breakpoint]}>
                  /mo{canAccessTotalMonthlyPriceWithFee ? ' ' : ''}
                </Text>
                {Boolean(canAccessTotalMonthlyPriceWithFee) && (
                  <Popover
                    dividers={true}
                    shouldAwaitInteractionResponse={true}
                    tone="neutral"
                    {...baseRentPopoverContents({
                      currentProperty: listingDetails,
                      user,
                      triggerLocation: 'posting_path_review',
                      triggerObject: 'zrm_review_top_of_page',
                    })}
                    trigger={<TriggerText>(base rent)</TriggerText>}
                  />
                )}
              </List.Item>
              {/* ZRM-10673 - aria-hidden on divider <li>'s: contains no content for a screen reader */}
              <List.Item css={verticalDividerStyles} aria-hidden="true" />
              <List.Item css={{ display: 'inline' }} data-testid="WizardModularListing-Review-Bedrooms">
                <Text textStyle={styles.details[breakpoint]}>
                  {`${getBedroomDisplay(floorplans?.[0]?.numBedrooms) || nullPlaceholder}`}
                </Text>
                {isStudio === false && <Text textStyle={styles.detailsText[breakpoint]}>&nbsp;bd</Text>}
              </List.Item>
              <List.Item css={verticalDividerStyles} aria-hidden="true" />
              <List.Item css={{ display: 'inline' }} data-testid="WizardModularListing-Review-Bathrooms">
                <Text
                  textStyle={styles.details[breakpoint]}
                >{`${floorplans?.[0]?.numBathrooms || nullPlaceholder}`}</Text>
                <Text textStyle={styles.detailsText[breakpoint]}>&nbsp;ba</Text>
              </List.Item>
              <List.Item css={verticalDividerStyles} aria-hidden="true" />
              <List.Item css={{ display: 'inline' }} data-testid="WizardModularListing-Review-Sqft">
                <Text
                  textStyle={styles.details[breakpoint]}
                >{`${floorplans?.[0]?.squareFeet ? numberUtils.prettyPrintNumber(floorplans?.[0]?.squareFeet) : nullPlaceholder}`}</Text>
                <Text textStyle={styles.detailsText[breakpoint]}>&nbsp;sqft</Text>
              </List.Item>
            </List.Root>
          </Box>
          {isRoomForRent && !isScreenMobile && (
            <Box css={{ display: 'flex', marginTop: '200' }}>
              <Text textStyle={styles.details[breakpoint]}>Room for rent</Text>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderAddressHeaderSection = () =>
    isInReactivationFlow && isRentalProtectionEnabled && !listingDetails?.active ? (
      <Box css={{ display: 'flex', flexDirection: 'column' }}>
        {renderAddressDetails()}
        <Paragraph css={{ marginTop: '200', display: 'flex', alignItems: 'center' }}>
          {/* oxlint-disable-next-line constellation/no-raw-spacing-values */}
          <Box css={{ display: 'inline-block', width: '21px', marginRight: '200' }}>
            <Image src={RentGuaranteeShieldStar} alt="Rental Protection request started" width={21} />
          </Box>
          Rental Protection request started
          <TextButton onClick={() => setDisplayRentalProtectionSelectionsModal(true)} css={{ marginLeft: '200' }}>
            See selections
          </TextButton>
        </Paragraph>
      </Box>
    ) : (
      renderAddressDetails()
    );

  return (
    <Box
      css={{
        display: 'flex',
        alignItems: 'flex-start',
        mdDown: { flexDirection: 'column', alignItems: 'start' },
        minHeight: { base: '74px' },
      }}
    >
      {!isScreenMobile && (
        <Box css={{ overflow: 'hidden', ...propertyPhotoStyles }}>
          <ConstellationPropertyCard.Photo
            src={listingDetails?.primaryPhoto?.url ?? undefined}
            alt={`Listing photo for ${formattedStreet}`}
            fallbackImg={
              isLoading ? (
                <LoadingImage isLoading={true} loadingWidth="100%" loadingHeight="100%" />
              ) : (
                <ConstellationPropertyCard.PhotoFallback />
              )
            }
            css={{ width: '100%', height: '2600' }}
          />
        </Box>
      )}{' '}
      {renderAddressHeaderSection()}
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          marginLeft: { lg: '200' },
          marginTop: { mdDown: '200' },
        }}
      >
        <Box
          css={{
            display: 'flex',
            flexDirection: { base: 'column', xl: 'row' },
            gap: { xl: '400' },
            justifyContent: 'flex-end',
          }}
        >
          {previewListingCTA ? <Box css={{ marginBottom: { base: '200', lg: '0' } }}>{previewListingCTA}</Box> : null}
          {user?.features?.canAccessUserManagementApplicationPermission &&
            listingDetails?.applicationsSummary?.listingAlias !== '' && (
              <TextButton
                tone="neutral"
                size={styles.button[breakpoint]}
                asChild
                data-eventid={REVIEW_PAGE_EVENT_IDS.APPLICATIONS_CLICK}
              >
                <a href={`/rental-manager/applications/property/${listingAlias}`} onClick={handleApplicationsClick}>
                  {`Applications (${totalNumberOfAllApplications})`}
                </a>
              </TextButton>
            )}
          {user?.isPostingPathAdmin && (
            <TextButton tone="neutral" size={styles.button[breakpoint]} onClick={() => handleEditButtonClick(true)}>
              Admin tools
            </TextButton>
          )}
        </Box>
      </Box>
      {displayAddPropertyModal && (
        <AddPropertyModal isOpen={true} onClose={() => setDisplayAddPropertyModal(false)} behavior="Edit" />
      )}
      {displayRentalProtectionSelectionsModal && (
        <RentalProtectionSelectionsModal
          onClose={() => setDisplayRentalProtectionSelectionsModal(false)}
          premium={rentalProtectionPremium}
          monthlyRent={Number(price)}
          isSecurityDepositWaived={listingDetails?.rentGuarantee?.isSecurityDepositWaived ?? false}
          securityDepositAmount={listingDetails?.rentGuarantee?.securityDeposit || 0}
        />
      )}
    </Box>
  );
};

export default PropertyCard;
